import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import {
  TrendingUp,
  UserPlus,
  Rocket,
  Repeat,
  Lock,
  Share2,
  Activity,
  Users,
  Briefcase,
  Award,
  Calculator,
  CalendarOff,
  GraduationCap,
  MessageSquare,
  FileText,
  Building2,
  ArrowRightLeft,
} from 'lucide-react';

const MODULE_ICONS = {
  People: Users,
  Performance: Activity,
  Compétences: Award,
  Paie: Calculator,
  Absences: CalendarOff,
  Formation: GraduationCap,
};

function MetricCard({ icon: Icon, label, value, subtitle, accent = 'blue', rate, testId }) {
  const accents = {
    blue: 'from-blue-500/10 to-blue-500/5 text-blue-600',
    emerald: 'from-emerald-500/10 to-emerald-500/5 text-emerald-600',
    purple: 'from-purple-500/10 to-purple-500/5 text-purple-600',
    amber: 'from-amber-500/10 to-amber-500/5 text-amber-600',
  };
  return (
    <Card data-testid={testId} className="overflow-hidden border-slate-200">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</p>
            <div className="mt-2 flex items-baseline gap-2">
              <p className="text-3xl font-bold text-slate-900 tabular-nums">{value}</p>
              {typeof rate === 'number' && (
                <span className="text-sm font-medium text-emerald-600">{rate}%</span>
              )}
            </div>
            {subtitle && <p className="mt-1 text-xs text-slate-500">{subtitle}</p>}
          </div>
          <div className={`rounded-xl p-2.5 bg-gradient-to-br ${accents[accent]}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function GreyedCard({ icon: Icon, label, reason, badge = 'À venir', extras, testId }) {
  return (
    <Card data-testid={testId} className="overflow-hidden border-dashed border-slate-300 bg-slate-50/60 opacity-80 transition hover:opacity-100">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</p>
              <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 text-[10px] py-0 px-2">
                <Lock className="h-3 w-3 mr-1" /> {badge}
              </Badge>
            </div>
            <p className="mt-2 text-2xl font-bold text-slate-400 tabular-nums">—</p>
            {reason && <p className="mt-2 text-xs text-slate-500 leading-relaxed">{reason}</p>}
            {extras}
          </div>
          <div className="rounded-xl p-2.5 bg-slate-200 text-slate-400">
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function FrequencyTable({ windows }) {
  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Repeat className="h-4 w-4 text-blue-600" />
          Demo — fréquence de connexion après inscription
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Fenêtre</th>
                <th className="text-right px-5 py-3 font-medium">≥ 2 connexions</th>
                <th className="text-right px-5 py-3 font-medium">≥ 5 connexions</th>
                <th className="text-right px-5 py-3 font-medium">≥ 10 connexions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {windows.map((w) => (
                <tr key={w.window} className="hover:bg-slate-50/60">
                  <td className="px-5 py-3 font-medium text-slate-700">{w.window}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{w.threshold2}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{w.threshold5}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{w.threshold10}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

export default function SuperAdminGrowthInsights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await api.get('/admin/growth');
        if (mounted) setData(r.data);
      } catch (e) {
        if (mounted) setError(e?.response?.data?.detail || 'Erreur de chargement');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-32" />)}
      </div>
    );
  }
  if (error) {
    return (
      <Card><CardContent className="p-6 text-red-600">{error}</CardContent></Card>
    );
  }
  if (!data) return null;

  const av = data.available;
  const cs = data.comingSoon;

  return (
    <div className="space-y-6" data-testid="growth-insights-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Indicateurs Croissance & Adoption</h2>
          <p className="text-sm text-slate-500">
            Suivi acquisition Demo, conversion vers PRO, engagement, recommandations.
          </p>
        </div>
        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
          <Activity className="h-3 w-3 mr-1" />
          Live · MAJ {new Date(data.generatedAt).toLocaleTimeString('fr-FR')}
        </Badge>
      </div>

      {/* Funnel public → compte (acquisition) */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Acquisition — Funnel public</h3>
          <Link
            to="/prospects"
            data-testid="open-prospects-link"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-violet-600 hover:text-violet-700 hover:underline"
          >
            Voir toutes les demandes
            <span aria-hidden>→</span>
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            testId="kpi-demo-requests"
            icon={MessageSquare}
            label={av.demoRequests.label}
            value={av.demoRequests.value}
            subtitle={av.demoRequests.subtitle}
            accent="blue"
          />
          <MetricCard
            testId="kpi-quote-requests"
            icon={FileText}
            label={av.quoteRequests.label}
            value={av.quoteRequests.value}
            subtitle={av.quoteRequests.subtitle}
            accent="amber"
          />
          <MetricCard
            testId="kpi-accounts-created"
            icon={Building2}
            label={av.accountsCreated.label}
            value={av.accountsCreated.value}
            subtitle={av.accountsCreated.subtitle}
            accent="emerald"
          />
          <MetricCard
            testId="kpi-request-conversion"
            icon={ArrowRightLeft}
            label={av.requestToAccountConversion.label}
            value={av.requestToAccountConversion.value}
            subtitle={av.requestToAccountConversion.subtitle}
            rate={av.requestToAccountConversion.rate}
            accent="purple"
          />
        </div>
      </div>

      {/* Engagement — Live KPIs */}
      <div>
        <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">Engagement & Conversion</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <MetricCard
            testId="kpi-demo-users"
            icon={UserPlus}
            label={av.demoUsersTotal.label}
            value={av.demoUsersTotal.value}
            subtitle={av.demoUsersTotal.subtitle}
            accent="blue"
          />
          <MetricCard
            testId="kpi-conversion"
            icon={Rocket}
            label={av.conversionDemoToPro.label}
            value={av.conversionDemoToPro.value}
            subtitle={av.conversionDemoToPro.subtitle}
            accent="emerald"
          />
          <MetricCard
            testId="kpi-first-login"
            icon={TrendingUp}
            label={av.demoFirstLogin.label}
            value={av.demoFirstLogin.value}
            subtitle={av.demoFirstLogin.subtitle}
            rate={av.demoFirstLogin.rate}
            accent="purple"
          />
        </div>
      </div>

      {/* Frequency Table */}
      <FrequencyTable windows={av.demoLoginFrequency.windows} />

      {/* Coming Soon — greyed */}
      <div>
        <div className="mb-3 flex items-center gap-2">
          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">À venir</h3>
          <span className="text-xs text-slate-400">— données collectées dès que la feature est instrumentée</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <GreyedCard
            testId="kpi-module-adoption"
            icon={Briefcase}
            label="Adoption par module"
            reason={cs.moduleAdoption.reason}
            badge={cs.moduleAdoption.active ? 'Live' : 'À venir'}
            extras={
              <div className="mt-3 grid grid-cols-2 gap-1.5">
                {(cs.moduleAdoption.data || []).map((m) => {
                  const Ic = MODULE_ICONS[m.module] || Briefcase;
                  const active = m.totalViews > 0;
                  return (
                    <div key={m.module} className={`flex items-center justify-between gap-2 text-[11px] border rounded-md px-2 py-1 ${active ? 'bg-white border-emerald-200 text-slate-700' : 'bg-white/60 border-slate-200 text-slate-400'}`}>
                      <span className="flex items-center gap-1.5">
                        <Ic className="h-3 w-3" />
                        {m.module}
                      </span>
                      <span className="tabular-nums font-semibold">{m.uniqueUsers}</span>
                    </div>
                  );
                })}
              </div>
            }
          />
          <GreyedCard
            testId="kpi-demo-referrals"
            icon={Share2}
            label={cs.demoReferrals.label}
            reason={cs.demoReferrals.reason}
          />
          <GreyedCard
            testId="kpi-pro-referrals"
            icon={Share2}
            label={cs.proReferrals.label}
            reason={cs.proReferrals.reason}
          />
        </div>
      </div>
    </div>
  );
}
